import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import {
  buscarChipsInactivos,
  buscarModemsInactivos,
  crearProgramacionEquipo,
  desprogramarEquipoProgramado,
  editarEquipoProgramado,
  listarEquiposProgramados,
  listarPlataformasRastreo,
  listarProgramacionesEquipo,
} from "../services/programacionEquipoService";
import { createWialonUnit, listWialonHwTypes } from "../../wialon/services/wialonUsers";
import { lcCreateUnidad, lcListHwTypes } from "../../leasecontrol/services/leasecontrolUsuarios.service";
import { listarCuentasFlespi, listarTiposFlespi, registrarDispositivosFlespi } from "../../flespi/services/flespiService";

const compactInputSx = {
  "& .MuiInputBase-input": { fontSize: "0.78rem", py: 0.7 },
  "& .MuiSelect-select": { fontSize: "0.78rem", py: 0.7 },
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/La_Paz",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value || "00";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
};

const valueOrDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const unwrapPayload = (response) => {
  const body = response?.data;
  if (body && typeof body === "object" && "data" in body) return body.data;
  return body;
};

const parseSensores = (value) => {
  if (!value) return { ...defaultSensores };
  if (typeof value === "object") return { ...defaultSensores, ...value };
  try {
    return { ...defaultSensores, ...JSON.parse(value) };
  } catch {
    return { ...defaultSensores };
  }
};

const defaultSensores = {
  acelerometro: false,
  temperatura: false,
  ibutton: false,
  buzer: false,
  corte_motor: false,
  boton_panico: false,
  sensor_puerta: false,
};

const sensorLabels = [
  ["acelerometro", "Acelerómetro"],
  ["temperatura", "Temperatura"],
  ["ibutton", "iButton"],
  ["buzer", "Buzer"],
  ["corte_motor", "Corte motor"],
  ["boton_panico", "Botón pánico"],
  ["sensor_puerta", "Sensor puerta"],
];

function TabPanel({ active, children }) {
  return (
    <Box role="tabpanel" hidden={!active} sx={{ display: active ? "block" : "none" }}>
      {children}
    </Box>
  );
}

function GenericProgramacionesTable({ rows }) {
  const columns = useMemo(() => {
    const preferred = [
      "id",
      "tipo_ot",
      "num_sim",
      "modem",
      "chip",
      "numero",
      "servidor",
      "plataforma",
      "estado",
      "fecha_registro",
      "fecha",
      "usu",
      "usuario",
      "observacion",
      "observacion_conclusion",
    ];
    const keys = Array.from(new Set(rows.flatMap((row) => Object.keys(row || {}))));
    const ordered = preferred.filter((key) => keys.includes(key));
    return [...ordered, ...keys.filter((key) => !ordered.includes(key))].slice(0, 14);
  }, [rows]);

  return (
    <TableContainer>
      <Table size="small" sx={{ "& th, & td": { px: 0.75, py: 0.55, fontSize: "0.74rem" } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f1f5f9" }}>
            {columns.map((col) => (
              <TableCell key={col} sx={{ fontWeight: 900 }}>
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={Math.max(columns.length, 1)} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                No hay registros para mostrar.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => (
              <TableRow key={row.id || index} hover>
                {columns.map((col) => (
                  <TableCell key={col}>{col.includes("fecha") ? formatDateTime(row[col]) : valueOrDash(row[col])}</TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function EquiposProgramadosTable({ rows, onEdit, onDesprogramar }) {
  return (
    <TableContainer>
      <Table size="small" sx={{ "& th, & td": { px: 0.75, py: 0.55, fontSize: "0.74rem" } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f1f5f9" }}>
            <TableCell sx={{ fontWeight: 900 }}>Módem</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>EMAI</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Modelo</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Chip</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Modelo chip</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Plataforma</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Ciudad</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Estado</TableCell>
            <TableCell sx={{ fontWeight: 900, textAlign: "right" }}>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                No hay equipos programados.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.num_sim} hover>
                <TableCell>
                  <Typography sx={{ fontSize: "0.74rem", fontWeight: 800 }}>{valueOrDash(row.num_sim)}</Typography>
                  <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>ID: {valueOrDash(row.id)}</Typography>
                </TableCell>
                <TableCell>{valueOrDash(row.emai)}</TableCell>
                <TableCell>{valueOrDash(row.modelo)}</TableCell>
                <TableCell>{valueOrDash(row.chip)}</TableCell>
                <TableCell>{valueOrDash(row.chip_modelo)}</TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: "0.74rem" }}>{valueOrDash(row.plataforma_rastreo || row.servidor || row.plataforma)}</Typography>
                  {row.flespi_cuenta && (
                    <Typography sx={{ fontSize: "0.66rem", color: "text.secondary" }}>{row.flespi_cuenta}</Typography>
                  )}
                </TableCell>
                <TableCell>{valueOrDash(row.ciudad)}</TableCell>
                <TableCell>
                  <Chip size="small" color="warning" label={valueOrDash(row.estado)} sx={{ height: 20, fontSize: "0.68rem" }} />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <IconButton size="small" color="primary" onClick={() => onEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => onDesprogramar(row)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SelectorTable({ type, rows, selectedKey, onSelect }) {
  const isModem = type === "modem";
  return (
    <TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: 1, maxHeight: 220 }}>
      <Table size="small" stickyHeader sx={{ "& th, & td": { px: 0.65, py: 0.45, fontSize: "0.72rem" } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 900, width: 40 }} />
            <TableCell sx={{ fontWeight: 900 }}>{isModem ? "Módem" : "Chip"}</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>EMAI</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Modelo</TableCell>
            <TableCell sx={{ fontWeight: 900 }}>Ciudad</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                Sin resultados.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const key = isModem ? row.num_sim : row.numero;
              const selected = String(selectedKey || "") === String(key || "");
              return (
                <TableRow
                  key={key}
                  hover
                  selected={selected}
                  onClick={() => onSelect(row)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell sx={{ px: 0 }}>
                    <Radio
                      size="small"
                      checked={selected}
                      onChange={() => onSelect(row)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0.25 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 800 }}>{valueOrDash(key)}</Typography>
                    {isModem && <Typography sx={{ fontSize: "0.66rem", color: "text.secondary" }}>ID: {valueOrDash(row.id)}</Typography>}
                  </TableCell>
                  <TableCell>{valueOrDash(row.emai)}</TableCell>
                  <TableCell>{valueOrDash(row.modelo)}</TableCell>
                  <TableCell>{valueOrDash(row.ciudad)}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ProgramacionEquipoPage() {
  const { user } = useAuth();
  const sucursalUsuario = user?.sucursal || "";
  const [tab, setTab] = useState(0);
  const [programaciones, setProgramaciones] = useState([]);
  const [programacionesTotal, setProgramacionesTotal] = useState(0);
  const [programacionesPage, setProgramacionesPage] = useState(0);
  const [programacionesLimit, setProgramacionesLimit] = useState(30);
  const [buscarProgramacion, setBuscarProgramacion] = useState("");
  const [buscarProgramacionAplicado, setBuscarProgramacionAplicado] = useState("");
  const [programados, setProgramados] = useState([]);
  const [programadosTotal, setProgramadosTotal] = useState(0);
  const [programadosPage, setProgramadosPage] = useState(0);
  const [programadosLimit, setProgramadosLimit] = useState(30);
  const [buscarProgramado, setBuscarProgramado] = useState("");
  const [buscarProgramadoAplicado, setBuscarProgramadoAplicado] = useState("");
  const [loadingProgramaciones, setLoadingProgramaciones] = useState(false);
  const [loadingProgramados, setLoadingProgramados] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [plataformasRastreo, setPlataformasRastreo] = useState([]);
  const [modems, setModems] = useState([]);
  const [chips, setChips] = useState([]);
  const [buscarModem, setBuscarModem] = useState("");
  const [buscarChip, setBuscarChip] = useState("");
  const [selectedModem, setSelectedModem] = useState(null);
  const [selectedChip, setSelectedChip] = useState(null);
  const [selectedEquipos, setSelectedEquipos] = useState([]);
  const [sensores, setSensores] = useState(defaultSensores);
  const [selectedPlataformaRastreo, setSelectedPlataformaRastreo] = useState("");
  // Cargar tambien en la plataforma real (ademas de dejar la etiqueta
  // informativa en plataforma_rastreo) — solo tiene sentido con un equipo a
  // la vez, ya que Wialon/Leasecontrol/Flespi piden datos por unidad
  // (tipo de hardware, cuenta, etc.), no por lote.
  const [cargarEnPlataforma, setCargarEnPlataforma] = useState(false);
  const [hwTypes, setHwTypes] = useState([]);
  const [hwTypeId, setHwTypeId] = useState("");
  const [loadingHwTypes, setLoadingHwTypes] = useState(false);
  const [flespiCuentas, setFlespiCuentas] = useState([]);
  const [flespiCuentaTipo, setFlespiCuentaTipo] = useState("");
  const [flespiTipos, setFlespiTipos] = useState([]);
  const [flespiDeviceTypeId, setFlespiDeviceTypeId] = useState("");
  const [observacion, setObservacion] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editPlataformaRastreo, setEditPlataformaRastreo] = useState("");
  const [editFlespiCuentaTipo, setEditFlespiCuentaTipo] = useState("");
  const [editFlespiCuentas, setEditFlespiCuentas] = useState([]);
  const [editObservacion, setEditObservacion] = useState("");
  const [editSensores, setEditSensores] = useState(defaultSensores);
  const [editBuscarChip, setEditBuscarChip] = useState("");
  const [editChips, setEditChips] = useState([]);
  const [editSelectedChip, setEditSelectedChip] = useState(null);
  const [editError, setEditError] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [desprogramarRow, setDesprogramarRow] = useState(null);
  const [desprogramarObs, setDesprogramarObs] = useState("");
  const [savingDesprogramar, setSavingDesprogramar] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const loadProgramaciones = useCallback(async () => {
    setLoadingProgramaciones(true);
    setError("");
    try {
      const response = await listarProgramacionesEquipo({
        page: programacionesPage + 1,
        limit: programacionesLimit,
        buscar: buscarProgramacionAplicado,
      });
      const payload = unwrapPayload(response) || {};
      setProgramaciones(Array.isArray(payload.items) ? payload.items : []);
      setProgramacionesTotal(Number(payload.total || 0));
    } catch (e) {
      setProgramaciones([]);
      setProgramacionesTotal(0);
      setError(e?.response?.data?.message || e?.message || "No se pudo cargar programaciones.");
    } finally {
      setLoadingProgramaciones(false);
    }
  }, [programacionesPage, programacionesLimit, buscarProgramacionAplicado]);

  const loadProgramados = useCallback(async () => {
    setLoadingProgramados(true);
    setError("");
    try {
      const response = await listarEquiposProgramados({
        page: programadosPage + 1,
        limit: programadosLimit,
        buscar: buscarProgramadoAplicado,
      });
      const payload = unwrapPayload(response) || {};
      setProgramados(Array.isArray(payload.items) ? payload.items : []);
      setProgramadosTotal(Number(payload.total || 0));
    } catch (e) {
      setProgramados([]);
      setProgramadosTotal(0);
      setError(e?.response?.data?.message || e?.message || "No se pudo cargar equipos programados.");
    } finally {
      setLoadingProgramados(false);
    }
  }, [programadosPage, programadosLimit, buscarProgramadoAplicado]);

  const loadDialogData = useCallback(async () => {
    const [rastreoResp, modemResp, chipResp] = await Promise.all([
      listarPlataformasRastreo(),
      buscarModemsInactivos({ limit: 20, sucursal: sucursalUsuario }),
      buscarChipsInactivos({ limit: 20, sucursal: sucursalUsuario }),
    ]);
    setPlataformasRastreo(unwrapPayload(rastreoResp) || []);
    setModems(unwrapPayload(modemResp) || []);
    setChips(unwrapPayload(chipResp) || []);
  }, [sucursalUsuario]);

  useEffect(() => {
    loadProgramaciones();
  }, [loadProgramaciones]);

  useEffect(() => {
    loadProgramados();
  }, [loadProgramados]);

  useEffect(() => {
    let active = true;
    listarPlataformasRastreo()
      .then((response) => { if (active) setPlataformasRastreo(unwrapPayload(response) || []); })
      .catch(() => { if (active) setPlataformasRastreo([]); });
    return () => {
      active = false;
    };
  }, []);

  const openDialog = async () => {
    setDialogOpen(true);
    setError("");
    setDialogError("");
    setSelectedModem(null);
    setSelectedChip(null);
    setSelectedEquipos([]);
    setSensores(defaultSensores);
    setSelectedPlataformaRastreo("");
    setCargarEnPlataforma(false);
    setHwTypeId("");
    setFlespiCuentaTipo("");
    setFlespiDeviceTypeId("");
    setObservacion("");
    setBuscarModem("");
    setBuscarChip("");
    try {
      await loadDialogData();
    } catch (e) {
      setDialogError(e?.response?.data?.message || e?.message || "No se pudo cargar el formulario.");
    }
  };

  const searchModems = async () => {
    const response = await buscarModemsInactivos({ buscar: buscarModem, limit: 30, sucursal: sucursalUsuario });
    setModems(unwrapPayload(response) || []);
  };

  const searchChips = async () => {
    const response = await buscarChipsInactivos({ buscar: buscarChip, limit: 30, sucursal: sucursalUsuario });
    setChips(unwrapPayload(response) || []);
  };

  const addSelectedEquipo = () => {
    if (!selectedModem?.num_sim) {
      setDialogError("Debe seleccionar un módem antes de agregar.");
      return;
    }
    if (!selectedChip?.numero) {
      setDialogError("Debe seleccionar un chip antes de agregar.");
      return;
    }
    if (selectedEquipos.some((item) => String(item.num_sim) === String(selectedModem.num_sim))) {
      setDialogError("Ese módem ya está agregado.");
      return;
    }
    if (selectedEquipos.some((item) => String(item.chip) === String(selectedChip.numero))) {
      setDialogError("Ese chip ya está agregado.");
      return;
    }

    setDialogError("");
    setSelectedEquipos((prev) => [
      ...prev,
      {
        num_sim: selectedModem.num_sim,
        modem_id: selectedModem.id,
        modem_emai: selectedModem.emai,
        modem_modelo: selectedModem.modelo,
        chip: selectedChip.numero,
        chip_emai: selectedChip.emai,
        chip_modelo: selectedChip.modelo,
      },
    ]);
    setSelectedModem(null);
    setSelectedChip(null);
  };

  const removeSelectedEquipo = (numSim) => {
    setSelectedEquipos((prev) => prev.filter((item) => String(item.num_sim) !== String(numSim)));
  };

  const toggleSensor = (key) => {
    setSensores((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // "Cargar tambien en la plataforma" solo tiene sentido con un equipo a la
  // vez y una plataforma elegida — con 0 o 2+ equipos en la lista se apaga
  // solo para no dejar el checkbox marcado sobre un estado que ya no aplica.
  const puedeCargarEnPlataforma = selectedEquipos.length === 1 && !!selectedPlataformaRastreo;
  const plataformaEsWialonOLc = selectedPlataformaRastreo === "Wialon" || selectedPlataformaRastreo === "LeaseControl";
  const plataformaEsFlespi = selectedPlataformaRastreo === "Flespi 1" || selectedPlataformaRastreo === "Flespi 2";

  useEffect(() => {
    if (!puedeCargarEnPlataforma && cargarEnPlataforma) setCargarEnPlataforma(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeCargarEnPlataforma]);

  useEffect(() => {
    if (!cargarEnPlataforma || !plataformaEsWialonOLc) {
      setHwTypes([]);
      return undefined;
    }
    let active = true;
    setLoadingHwTypes(true);
    const fetchHwTypes = selectedPlataformaRastreo === "Wialon" ? listWialonHwTypes : lcListHwTypes;
    fetchHwTypes({ limit: 200 })
      .then((res) => { if (active) setHwTypes(res?.data?.data || []); })
      .catch(() => { if (active) setHwTypes([]); })
      .finally(() => { if (active) setLoadingHwTypes(false); });
    return () => { active = false; };
  }, [cargarEnPlataforma, plataformaEsWialonOLc, selectedPlataformaRastreo]);

  // La cuenta Flespi (a cuál de las YPFB Internacional/Nacional/etc. va el
  // equipo) hace falta elegirla SIEMPRE que la plataforma sea Flespi 1/2 —
  // no solo cuando se va a registrar de una en Flespi. El tipo de
  // dispositivo, en cambio, solo hace falta para esa llamada real.
  useEffect(() => {
    if (!plataformaEsFlespi) {
      setFlespiCuentas([]);
      return undefined;
    }
    let active = true;
    const origen = selectedPlataformaRastreo === "Flespi 1" ? "flespi" : "flespi2";
    listarCuentasFlespi()
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setFlespiCuentas(rows.filter((r) => r.origen === origen && r.rol !== "admin"));
      })
      .catch(() => { if (active) setFlespiCuentas([]); });
    return () => { active = false; };
  }, [plataformaEsFlespi, selectedPlataformaRastreo]);

  useEffect(() => {
    if (!cargarEnPlataforma || !plataformaEsFlespi) {
      setFlespiTipos([]);
      return undefined;
    }
    let active = true;
    const origen = selectedPlataformaRastreo === "Flespi 1" ? "flespi" : "flespi2";
    listarTiposFlespi(origen)
      .then((res) => { if (active) setFlespiTipos(res?.data?.data || []); })
      .catch(() => { if (active) setFlespiTipos([]); });
    return () => { active = false; };
  }, [cargarEnPlataforma, plataformaEsFlespi, selectedPlataformaRastreo]);

  const editPlataformaEsFlespi = editPlataformaRastreo === "Flespi 1" || editPlataformaRastreo === "Flespi 2";

  useEffect(() => {
    if (!editDialogOpen || !editPlataformaEsFlespi) {
      setEditFlespiCuentas([]);
      return undefined;
    }
    let active = true;
    const origen = editPlataformaRastreo === "Flespi 1" ? "flespi" : "flespi2";
    listarCuentasFlespi()
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setEditFlespiCuentas(rows.filter((r) => r.origen === origen && r.rol !== "admin"));
      })
      .catch(() => { if (active) setEditFlespiCuentas([]); });
    return () => { active = false; };
  }, [editDialogOpen, editPlataformaEsFlespi, editPlataformaRastreo]);

  const saveProgramacion = async () => {
    if (selectedEquipos.length === 0) {
      setDialogError("Debe agregar al menos un equipo.");
      return;
    }
    if (!selectedPlataformaRastreo) {
      setDialogError("Debe seleccionar una plataforma.");
      return;
    }
    if (cargarEnPlataforma && plataformaEsWialonOLc && !hwTypeId) {
      setDialogError("Debe seleccionar el tipo de hardware.");
      return;
    }
    if (plataformaEsFlespi && !flespiCuentaTipo) {
      setDialogError("Debe seleccionar la cuenta de Flespi.");
      return;
    }
    if (cargarEnPlataforma && plataformaEsFlespi && !flespiDeviceTypeId) {
      setDialogError("Debe seleccionar el tipo de dispositivo de Flespi.");
      return;
    }
    setSaving(true);
    setDialogError("");
    try {
      const equipo = selectedEquipos[0];

      if (cargarEnPlataforma && plataformaEsWialonOLc) {
        // Wialon/Leasecontrol ya hacen todo en una sola llamada: crean la
        // unidad remota Y programan el modem/chip local (mismo endpoint que
        // usa el Panel Global para "Registrar unidad") — por eso NO se llama
        // ademas a crearProgramacionEquipo, que fallaria al encontrar el
        // chip ya Programado.
        const crearUnidad = selectedPlataformaRastreo === "Wialon" ? createWialonUnit : lcCreateUnidad;
        await crearUnidad({
          modem: equipo.num_sim,
          chipNumero: equipo.chip,
          hwTypeId,
          observacion,
        });
      } else if (cargarEnPlataforma && plataformaEsFlespi) {
        // Flespi, en cambio, ya no toca equipo_modem/equipo_chip (a pedido,
        // sesion anterior) — se puede programar localmente Y registrar en
        // Flespi sin que se pisen entre si.
        await crearProgramacionEquipo({
          equipos: [{ num_sim: equipo.num_sim, chip: equipo.chip }],
          plataforma_rastreo: selectedPlataformaRastreo,
          flespi_cuenta: flespiCuentaTipo,
          sensores,
          observacion,
        });
        await registrarDispositivosFlespi({
          origen: selectedPlataformaRastreo === "Flespi 1" ? "flespi" : "flespi2",
          tipo: flespiCuentaTipo,
          devices: [{
            ident: equipo.num_sim,
            name: equipo.modem_modelo || equipo.num_sim,
            device_type_id: flespiDeviceTypeId,
            messages_rotate: 1024,
          }],
        });
      } else {
        await crearProgramacionEquipo({
          equipos: selectedEquipos.map((item) => ({ num_sim: item.num_sim, chip: item.chip })),
          plataforma_rastreo: selectedPlataformaRastreo,
          flespi_cuenta: plataformaEsFlespi ? flespiCuentaTipo : undefined,
          sensores,
          observacion,
        });
      }

      setDialogOpen(false);
      setSnack({ open: true, severity: "success", message: "Equipo programado correctamente." });
      setProgramacionesPage(0);
      setProgramadosPage(0);
      await Promise.all([loadProgramaciones(), loadProgramados()]);
    } catch (e) {
      setDialogError(e?.response?.data?.error || e?.response?.data?.message || e?.message || "No se pudo programar el equipo.");
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (row) => {
    setEditRow(row);
    setEditPlataformaRastreo(row.plataforma_rastreo || "");
    setEditFlespiCuentaTipo(row.flespi_cuenta || "");
    setEditObservacion(row.observacion || "");
    setEditSensores(parseSensores(row.sensores));
    setEditBuscarChip("");
    setEditChips([]);
    setEditSelectedChip(null);
    setEditError("");
    setEditDialogOpen(true);
  };

  const searchEditChips = async () => {
    const response = await buscarChipsInactivos({ buscar: editBuscarChip, limit: 30, sucursal: sucursalUsuario });
    setEditChips(unwrapPayload(response) || []);
  };

  const toggleEditSensor = (key) => {
    setEditSensores((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const saveEditProgramacion = async () => {
    if (!editRow?.num_sim) return;
    if (!editPlataformaRastreo) {
      setEditError("Debe seleccionar una plataforma.");
      return;
    }
    if (editPlataformaEsFlespi && !editFlespiCuentaTipo) {
      setEditError("Debe seleccionar la cuenta de Flespi.");
      return;
    }
    setSavingEdit(true);
    setEditError("");
    try {
      await editarEquipoProgramado(editRow.num_sim, {
        plataforma_rastreo: editPlataformaRastreo,
        flespi_cuenta: editPlataformaEsFlespi ? editFlespiCuentaTipo : undefined,
        chip: editSelectedChip?.numero || "",
        observacion: editObservacion,
        sensores: editSensores,
      });
      setEditDialogOpen(false);
      setSnack({ open: true, severity: "success", message: "Programación actualizada." });
      await Promise.all([loadProgramaciones(), loadProgramados()]);
    } catch (e) {
      setEditError(e?.response?.data?.message || e?.message || "No se pudo actualizar la programación.");
    } finally {
      setSavingEdit(false);
    }
  };

  const openDesprogramarDialog = (row) => {
    setDesprogramarRow(row);
    setDesprogramarObs("");
  };

  const confirmDesprogramar = async () => {
    if (!desprogramarRow?.num_sim) return;
    setSavingDesprogramar(true);
    try {
      await desprogramarEquipoProgramado(desprogramarRow.num_sim, { observacion: desprogramarObs });
      setDesprogramarRow(null);
      setSnack({ open: true, severity: "success", message: "Equipo desprogramado." });
      await Promise.all([loadProgramaciones(), loadProgramados()]);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo desprogramar el equipo.");
    } finally {
      setSavingDesprogramar(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Programación de equipos"
        subtitle="Preparación de módem y chip para procesarlos en requerimientos"
        backTo="/dashboard"
      />

      <Box sx={{ p: { xs: 1, md: 1.25 } }}>
        {error && (
          <Alert severity="error" onClose={() => setError("")} sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ mb: 1, overflow: "hidden", borderRadius: 1 }}>
          <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "stretch", md: "center" }}>
            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 34,
                flex: 1,
                "& .MuiTab-root": {
                  minHeight: 34,
                  py: 0.45,
                  px: 1.25,
                  fontSize: 12,
                  textTransform: "none",
                },
              }}
            >
              <Tab label="Programaciones" />
              <Tab label="Equipos programados" />
            </Tabs>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openDialog}
              sx={{ m: 0.75, minHeight: 32, bgcolor: "#006d77", "&:hover": { bgcolor: "#00565f" } }}
            >
              Agregar programación
            </Button>
          </Stack>
        </Paper>

        <TabPanel active={tab === 0}>
          <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={0.75} alignItems={{ xs: "stretch", md: "center" }} sx={{ p: 1 }}>
              <TextField
                size="small"
                label="Buscar"
                value={buscarProgramacion}
                onChange={(e) => setBuscarProgramacion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setProgramacionesPage(0);
                    setBuscarProgramacionAplicado(buscarProgramacion.trim());
                  }
                }}
                sx={{ minWidth: { md: 320 }, ...compactInputSx }}
              />
              <Button
                size="small"
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => {
                  setProgramacionesPage(0);
                  setBuscarProgramacionAplicado(buscarProgramacion.trim());
                }}
                sx={{ minHeight: 32, bgcolor: "#006d77", "&:hover": { bgcolor: "#00565f" } }}
              >
                Buscar
              </Button>
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={loadProgramaciones} sx={{ minHeight: 32 }}>
                Actualizar
              </Button>
              <Chip size="small" label={`Total: ${programacionesTotal}`} />
            </Stack>
            {loadingProgramaciones ? (
              <Typography sx={{ p: 1, fontSize: "0.78rem", color: "text.secondary" }}>Cargando...</Typography>
            ) : (
              <GenericProgramacionesTable rows={programaciones} />
            )}
            <TablePagination
              component="div"
              count={programacionesTotal}
              page={programacionesPage}
              rowsPerPage={programacionesLimit}
              onPageChange={(_, nextPage) => setProgramacionesPage(nextPage)}
              onRowsPerPageChange={(e) => {
                setProgramacionesLimit(Number(e.target.value));
                setProgramacionesPage(0);
              }}
              rowsPerPageOptions={[15, 30, 50, 100]}
              labelRowsPerPage="Límite"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </Paper>
        </TabPanel>

        <TabPanel active={tab === 1}>
          <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={0.75} alignItems={{ xs: "stretch", md: "center" }} sx={{ p: 1 }}>
              <TextField
                size="small"
                label="Buscar equipo programado"
                value={buscarProgramado}
                onChange={(e) => setBuscarProgramado(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setProgramadosPage(0);
                    setBuscarProgramadoAplicado(buscarProgramado.trim());
                  }
                }}
                sx={{ minWidth: { md: 320 }, ...compactInputSx }}
              />
              <Button
                size="small"
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => {
                  setProgramadosPage(0);
                  setBuscarProgramadoAplicado(buscarProgramado.trim());
                }}
                sx={{ minHeight: 32, bgcolor: "#006d77", "&:hover": { bgcolor: "#00565f" } }}
              >
                Buscar
              </Button>
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={loadProgramados} sx={{ minHeight: 32 }}>
                Actualizar
              </Button>
              <Chip size="small" label={`Total: ${programadosTotal}`} />
            </Stack>
            {loadingProgramados ? (
              <Typography sx={{ p: 1, fontSize: "0.78rem", color: "text.secondary" }}>Cargando...</Typography>
            ) : (
              <EquiposProgramadosTable
                rows={programados}
                onEdit={openEditDialog}
                onDesprogramar={openDesprogramarDialog}
              />
            )}
            <TablePagination
              component="div"
              count={programadosTotal}
              page={programadosPage}
              rowsPerPage={programadosLimit}
              onPageChange={(_, nextPage) => setProgramadosPage(nextPage)}
              onRowsPerPageChange={(e) => {
                setProgramadosLimit(Number(e.target.value));
                setProgramadosPage(0);
              }}
              rowsPerPageOptions={[15, 30, 50, 100]}
              labelRowsPerPage="Límite"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </Paper>
        </TabPanel>
      </Box>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          Nueva programación
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
            Tipo OT fijo: 23{sucursalUsuario ? ` · Sucursal: ${sucursalUsuario}` : ""}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          <Stack spacing={1}>
            {dialogError && (
              <Alert severity="error" onClose={() => setDialogError("")}>
                {dialogError}
              </Alert>
            )}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
              <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
                <Stack direction="row" spacing={0.75} sx={{ mb: 0.75 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Buscar módem inactivo"
                    value={buscarModem}
                    onChange={(e) => setBuscarModem(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchModems()}
                    sx={compactInputSx}
                  />
                  <Button variant="outlined" size="small" onClick={searchModems} sx={{ minWidth: 42 }}>
                    <SearchIcon fontSize="small" />
                  </Button>
                </Stack>
                <SelectorTable type="modem" rows={modems} selectedKey={selectedModem?.num_sim} onSelect={setSelectedModem} />
              </Paper>

              <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
                <Stack direction="row" spacing={0.75} sx={{ mb: 0.75 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Buscar chip inactivo"
                    value={buscarChip}
                    onChange={(e) => setBuscarChip(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchChips()}
                    sx={compactInputSx}
                  />
                  <Button variant="outlined" size="small" onClick={searchChips} sx={{ minWidth: 42 }}>
                    <SearchIcon fontSize="small" />
                  </Button>
                </Stack>
                <SelectorTable type="chip" rows={chips} selectedKey={selectedChip?.numero} onSelect={setSelectedChip} />
              </Paper>
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addSelectedEquipo}
                disabled={!selectedModem?.num_sim || !selectedChip?.numero}
                sx={{ minHeight: 32, alignSelf: { md: "flex-start" } }}
              >
                Agregar equipo
              </Button>
              <Chip size="small" label={`Equipos agregados: ${selectedEquipos.length}`} />
              {selectedModem?.num_sim && <Chip size="small" variant="outlined" label={`Módem: ${selectedModem.num_sim}`} />}
              {selectedChip?.numero && <Chip size="small" variant="outlined" label={`Chip: ${selectedChip.numero}`} />}
            </Stack>

            <TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: 1 }}>
              <Table size="small" sx={{ "& th, & td": { px: 0.65, py: 0.45, fontSize: "0.72rem" } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 900 }}>Módem</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Modelo módem</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Chip</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Modelo chip</TableCell>
                    <TableCell sx={{ fontWeight: 900, textAlign: "right" }}>Quitar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEquipos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                        Agrega uno o más equipos antes de guardar.
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedEquipos.map((item) => (
                      <TableRow key={item.num_sim}>
                        <TableCell>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 800 }}>{item.num_sim}</Typography>
                          <Typography sx={{ fontSize: "0.66rem", color: "text.secondary" }}>{valueOrDash(item.modem_emai)}</Typography>
                        </TableCell>
                        <TableCell>{valueOrDash(item.modem_modelo)}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 800 }}>{item.chip}</Typography>
                          <Typography sx={{ fontSize: "0.66rem", color: "text.secondary" }}>{valueOrDash(item.chip_emai)}</Typography>
                        </TableCell>
                        <TableCell>{valueOrDash(item.chip_modelo)}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => removeSelectedEquipo(item.num_sim)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 900, mb: 0.25 }}>
                Sensores para los módems programados
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 0.25 }}>
                {sensorLabels.map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={<Checkbox size="small" checked={Boolean(sensores[key])} onChange={() => toggleSensor(key)} />}
                    label={label}
                    sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.73rem" } }}
                  />
                ))}
              </Box>
            </Paper>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: plataformaEsFlespi ? "200px 220px 1fr" : "220px 1fr" }, gap: 1 }}>
              <FormControl size="small" fullWidth sx={compactInputSx}>
                <InputLabel>Plataforma</InputLabel>
                <Select
                  label="Plataforma"
                  value={selectedPlataformaRastreo}
                  onChange={(e) => {
                    setSelectedPlataformaRastreo(e.target.value);
                    setFlespiCuentaTipo("");
                  }}
                >
                  {plataformasRastreo.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {plataformaEsFlespi && (
                <FormControl size="small" fullWidth sx={compactInputSx}>
                  <InputLabel>Cuenta Flespi</InputLabel>
                  <Select
                    label="Cuenta Flespi"
                    value={flespiCuentaTipo}
                    onChange={(e) => setFlespiCuentaTipo(e.target.value)}
                  >
                    {flespiCuentas.length === 0 ? (
                      <MenuItem value="" disabled>Sin cuentas disponibles</MenuItem>
                    ) : (
                      flespiCuentas.map((c) => (
                        <MenuItem key={c.nombre} value={c.nombre}>{c.detalle || c.nombre}</MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
              <TextField
                size="small"
                label="Observación"
                value={observacion}
                onChange={(e) => setObservacion(e.target.value)}
                sx={compactInputSx}
              />
            </Box>

            {puedeCargarEnPlataforma && (
              <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={cargarEnPlataforma}
                      onChange={(e) => setCargarEnPlataforma(e.target.checked)}
                    />
                  }
                  label={`Cargar también en ${selectedPlataformaRastreo} al programar`}
                  sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.78rem", fontWeight: 700 } }}
                />

                {cargarEnPlataforma && plataformaEsWialonOLc && (
                  <Box sx={{ mt: 1 }}>
                    <FormControl size="small" fullWidth sx={compactInputSx}>
                      <InputLabel>Tipo de hardware</InputLabel>
                      <Select
                        label="Tipo de hardware"
                        value={hwTypeId}
                        onChange={(e) => setHwTypeId(e.target.value)}
                        disabled={loadingHwTypes}
                      >
                        {loadingHwTypes ? (
                          <MenuItem value="" disabled>Cargando...</MenuItem>
                        ) : hwTypes.length === 0 ? (
                          <MenuItem value="" disabled>Sin tipos disponibles</MenuItem>
                        ) : (
                          hwTypes.map((t) => (
                            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {cargarEnPlataforma && plataformaEsFlespi && (
                  <Box sx={{ mt: 1 }}>
                    <FormControl size="small" fullWidth sx={compactInputSx}>
                      <InputLabel>Tipo de dispositivo</InputLabel>
                      <Select
                        label="Tipo de dispositivo"
                        value={flespiDeviceTypeId}
                        onChange={(e) => setFlespiDeviceTypeId(e.target.value)}
                      >
                        {flespiTipos.length === 0 ? (
                          <MenuItem value="" disabled>Sin tipos disponibles</MenuItem>
                        ) : (
                          flespiTipos.map((t) => (
                            <MenuItem key={t.id} value={t.id}>{t.nombre}</MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </Paper>
            )}

            <Alert severity="info" sx={{ py: 0 }}>
              {cargarEnPlataforma
                ? `Al guardar, además de programar el equipo localmente, se va a crear/registrar en ${selectedPlataformaRastreo}.`
                : "Al guardar, los módems y chips agregados pasarán a estado Programado y cada chip quedará relacionado a su módem."}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={saveProgramacion}
            disabled={saving || selectedEquipos.length === 0}
            sx={{ bgcolor: "#006d77", "&:hover": { bgcolor: "#00565f" } }}
          >
            {saving ? "Guardando..." : "Guardar programación"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => !savingEdit && setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          Editar programación
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
            Módem: {editRow?.num_sim || "-"} · Chip: {editRow?.chip || "-"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          <Stack spacing={1}>
            {editError && (
              <Alert severity="error" onClose={() => setEditError("")}>
                {editError}
              </Alert>
            )}
            <FormControl size="small" fullWidth sx={compactInputSx}>
              <InputLabel>Plataforma</InputLabel>
              <Select
                label="Plataforma"
                value={editPlataformaRastreo}
                onChange={(e) => {
                  setEditPlataformaRastreo(e.target.value);
                  setEditFlespiCuentaTipo("");
                }}
              >
                {plataformasRastreo.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {editPlataformaEsFlespi && (
              <FormControl size="small" fullWidth sx={compactInputSx}>
                <InputLabel>Cuenta Flespi</InputLabel>
                <Select
                  label="Cuenta Flespi"
                  value={editFlespiCuentaTipo}
                  onChange={(e) => setEditFlespiCuentaTipo(e.target.value)}
                >
                  {editFlespiCuentas.length === 0 ? (
                    <MenuItem value="" disabled>Sin cuentas disponibles</MenuItem>
                  ) : (
                    editFlespiCuentas.map((c) => (
                      <MenuItem key={c.nombre} value={c.nombre}>{c.detalle || c.nombre}</MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}
            <TextField
              size="small"
              label="Observación"
              value={editObservacion}
              onChange={(e) => setEditObservacion(e.target.value)}
              multiline
              minRows={2}
              sx={compactInputSx}
            />
            <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 900, mb: 0.5 }}>
                Cambiar chip
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mb: 0.75 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar chip inactivo"
                  value={editBuscarChip}
                  onChange={(e) => setEditBuscarChip(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchEditChips()}
                  sx={compactInputSx}
                />
                <Button variant="outlined" size="small" onClick={searchEditChips} sx={{ minWidth: 42 }}>
                  <SearchIcon fontSize="small" />
                </Button>
              </Stack>
              <SelectorTable
                type="chip"
                rows={editChips}
                selectedKey={editSelectedChip?.numero}
                onSelect={setEditSelectedChip}
              />
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
                <Chip size="small" label={`Actual: ${editRow?.chip || "-"}`} />
                {editSelectedChip?.numero && (
                  <Chip size="small" color="warning" label={`Nuevo: ${editSelectedChip.numero}`} />
                )}
                {editSelectedChip?.numero && (
                  <Button size="small" color="inherit" onClick={() => setEditSelectedChip(null)}>
                    Mantener actual
                  </Button>
                )}
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 900, mb: 0.25 }}>
                Sensores
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 0.25 }}>
                {sensorLabels.map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={<Checkbox size="small" checked={Boolean(editSensores[key])} onChange={() => toggleEditSensor(key)} />}
                    label={label}
                    sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.73rem" } }}
                  />
                ))}
              </Box>
            </Paper>
            <Alert severity="info" sx={{ py: 0 }}>
              Al guardar se actualizará el módem programado y se registrará historial de edición.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={savingEdit}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={saveEditProgramacion}
            disabled={savingEdit}
            sx={{ bgcolor: "#006d77", "&:hover": { bgcolor: "#00565f" } }}
          >
            {savingEdit ? "Guardando..." : "Guardar cambios"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!desprogramarRow} onClose={() => !savingDesprogramar && setDesprogramarRow(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          Desprogramar equipo
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
            Módem: {desprogramarRow?.num_sim || "-"} · Chip: {desprogramarRow?.chip || "-"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          <Stack spacing={1}>
            <Alert severity="warning" sx={{ py: 0 }}>
              El módem y el chip volverán a Inactivo, y se quitará la relación del chip con el módem.
            </Alert>
            <TextField
              size="small"
              label="Observación"
              value={desprogramarObs}
              onChange={(e) => setDesprogramarObs(e.target.value)}
              multiline
              minRows={2}
              sx={compactInputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button onClick={() => setDesprogramarRow(null)} disabled={savingDesprogramar}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={confirmDesprogramar} disabled={savingDesprogramar}>
            {savingDesprogramar ? "Desprogramando..." : "Desprogramar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={2800}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        message={snack.message}
      />
    </DashboardLayout>
  );
}
